<template>
  <b-modal size="lg" :visible="visible" no-close-on-backdrop no-close-on-esc centered>
    <div slot="modal-header" class="w-100">
      <h5>{{ title }}
        <button type="button" class="close"
                @click.prevent="closeModal()">
          <span aria-hidden="true">&times;</span></button>
      </h5>
    </div>
    <div class="container-fluid">
      <div class="row justify-content-center m-2">
        <div class="alert alert-danger col" role="alert" v-if="insurance.verification && insurance.verification.verificationError">
          <div>{{ insurance.verification.verificationError }}</div>
        </div>
        <div class="alert alert-danger col" role="alert" v-if="insurance.policyNumberFromVerification">
          <div>Policy Number Does Not Match</div>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <h5>Plan Details</h5>
          <b-form-group id="insuranceCompanyFieldGroup"
                        breakpoint="md"
                        label="Insurance Company"
                        label-for="insuranceCompanyField"
                        :state="stateForField('insuranceCompany')"
                        :invalid-feedback="errorForField('insuranceCompany')">
            <b-dropdown menu-class="dropdown-container" id="insuranceCompanyField" :text="insuranceCompanyName"
                        :variant="hasError('insuranceCompany') ? 'outline-danger' : 'outline-dark'">
              <b-dropdown-item v-for="company in orgInsurancesCompanies"
                               :key="company.organizationInsuranceCompanyId"
                               @click.prevent="setCompany(company)">{{ company.companyNameFromOrg }}
              </b-dropdown-item>
            </b-dropdown>
          </b-form-group>
          <b-form-group id="insurancePlanFieldGroup"
                        breakpoint="md"
                        label="Insurance Plan"
                        label-for="insurancePlanField"
                        :state="stateForField('insurancePlan')"
                        :invalid-feedback="errorForField('insurancePlan')">
            <b-dropdown @hidden="clearPlanSearch" menu-class="dropdown-container" id="insurancePlanField"
                        :text="insurancePlanName"
                        :variant="hasError('insurancePlan') ? 'outline-danger' : 'outline-dark'">
              <b-dropdown-form>
                <b-input-group>
                  <b-input placeholder="Filter" v-model="planSearchText" type="search"></b-input>
                  <b-input-group-append>
                    <b-button variant="outline-secondary">
                      <font-awesome-icon icon="filter"/>
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-dropdown-form>
              <b-dropdown-item v-for="plan in insurancePlans" :key="plan.id" @click.prevent="setPlan(plan)">
                <div class="insurance-plan-option">
                  <b>{{ plan.planNameFromOrg }}</b> ({{ plan.planIdFromOrg }})<br/>
                  {{ plan.street1 }} {{ plan.city }}, {{ plan.stateShort }} {{ plan.zip }}
                </div>
              </b-dropdown-item>
            </b-dropdown>
          </b-form-group>
          <b-form-group id="subscriberNumberFieldGroup"
                        breakpoint="md"
                        label="Subscriber ID"
                        label-for="subscriberNumberField">
            <b-form-input id="subscriberNumberField" v-model="insurance.policyNumber"></b-form-input>
          </b-form-group>
          <b-form-group id="groupNumberFieldGroup"
                        breakpoint="md"
                        label="Group Number"
                        label-for="groupNumberField">
            <b-form-input id="groupNumberField" v-model="insurance.groupNumber"></b-form-input>
          </b-form-group>
          <div v-if="insurance.policyNumberFromVerification">
            <b-form-group id="policyNumberFieldGroup"
                          breakpoint="md"
                          label="Subscriber ID from Payor"
                          label-for="policyNumberField">
              <span class="pt-0 pr-2"> <b> {{insurance.policyNumberFromVerification}} </b></span>
              <b-form-checkbox v-model="insurance.ignorePolicyNumberMismatch" class="ml-2 text-danger pt-1">Ignore Policy Number Mismatch</b-form-checkbox>

            </b-form-group>
          </div>
        </div>
        <div class="col">
          <h5>Subscriber</h5>
          <b-form-group
              label-cols="5"
              :state="stateForField('patientIsSubscriber')"
              :invalid-feedback="errorForField('patientIsSubscriber')"
              label="Patient Is Subscriber"
              class="mb-0">
            <b-form-radio-group v-model="insurance.patientIsSubscriber" class="pt-2" :options="yesNoOptions"/>
          </b-form-group>
          <div v-show="!insurance.patientIsSubscriber">
            <b-form-group id="InsuredContactTypeFieldGroup"
                          breakpoint="md"
                          label="Relationship to Patient"
                          label-for="insuredContactTypeField"
                          :state="stateForField('insuredContactTypeId')"
                          :invalid-feedback="errorForField('insuredContactTypeId')">
              <b-dropdown menu-class="dropdown-container" id="insuredContactTypeField" :text="insuredContactTypeName"
                          :variant="hasError('insuredContactTypeId') ? 'outline-danger' : 'outline-dark'">
                <b-dropdown-item v-for="contactType in insuredContactTypes"
                                 :key="contactType.insuredContactTypeId"
                                 @click.prevent="setContactType(contactType)">{{ contactType.name }}
                </b-dropdown-item>
              </b-dropdown>
            </b-form-group>
            <b-form-group id="subscriberFirstNameFieldGroup"
                          breakpoint="md"
                          :state="stateForField('insuredFirstName')"
                          :invalid-feedback="errorForField('insuredFirstName')"
                          label="First Name"
                          label-for="subscriberFirstNameField">
              <b-form-input id="subscriberFirstNameField" v-model="insurance.insuredFirstName"></b-form-input>
            </b-form-group>
            <b-form-group id="subscriberMiddleNameFieldGroup"
                          breakpoint="md"
                          label="Middle Name"
                          label-for="subscriberMiddleNameField">
              <b-form-input id="subscriberMiddleNameField" v-model="insurance.insuredMiddleName"></b-form-input>
            </b-form-group>
            <b-form-group id="subscriberLastNameFieldGroup"
                          breakpoint="md"
                          :state="stateForField('insuredLastName')"
                          :invalid-feedback="errorForField('insuredLastName')"
                          label="Last Name"
                          label-for="subscriberLastNameField">
              <b-form-input id="subscriberLastNameField" v-model="insurance.insuredLastName"></b-form-input>
            </b-form-group>
            <b-form-group id="subscriberBirthDateFieldGroup"
                          breakpoint="md"
                          label="Birth Date"
                          :state="stateForField('insuredBirthDate')"
                          :invalid-feedback="errorForField('insuredBirthDate')"
                          label-for="subscriberBirthDateField">
              <datepicker id="subscriberBirthDateField" format="MM/dd/yyyy" :typeable="true" :use-utc="true"
                          class="form-control datepicker" v-model="insurance.insuredBirthDate"></datepicker>
            </b-form-group>
            <b-form-group
                label-cols="5"
                :state="stateForField('insuredGenderId')"
                :invalid-feedback="errorForField('insuredGenderId')"
                label="Gender"
                class="mb-0">
              <b-form-radio-group v-model="insurance.insuredGenderId" class="pt-2" :options="genderOptions"/>
            </b-form-group>
          </div>
        </div>
      </div>
      <div v-if="false" v-show="insurance.verification && insurance.verification.insuranceEligibilityStatus === 'Verified'" class="row mt-2">
        <div class="col">
          <h5>Individual Coverage</h5>
          Deductible - ${{ insurance.verification.individualDeductibleMet }} of ${{insurance.verification.individualDeductible}}
          <div class="progress progress-orange">
            <div class="progress-bar" role="progressbar" :style="'width: ' + individualDeductiblePercent + '%'"></div>
          </div>
          Out of Pocket - ${{ insurance.verification.individualMaxOopMet }} of ${{insurance.verification.individualMaxOop}}
          <div class="progress progress-orange">
            <div class="progress-bar" role="progressbar" :style="'width: ' + individualOutOfPocketPercent + '%'"></div>
          </div>
        </div>
        <div class="col">
          <h5>Family Coverage</h5>
          Deductible - ${{ insurance.verification.familyDeductibleMet }} of ${{insurance.verification.familyDeductible}}
          <div class="progress progress-orange">
            <div class="progress-bar" role="progressbar" :style="'width: ' + familyDeductiblePercent + '%'"></div>
          </div>
          Out of Pocket - ${{ insurance.verification.familyMaxOopMet }} of ${{insurance.verification.familyMaxOop}}
          <div class="progress progress-orange">
            <div class="progress-bar" role="progressbar" :style="'width: ' + familyOutOfPocketPercent + '%'"></div>
          </div>
        </div>
      </div>
      <div v-show="frontImageUrl || backImageUrl" class="row mt-2">
        <div class="col">
          <h5>Insurance Front</h5>
          <img class="insurance-card" :src="frontImageUrl">
        </div>
        <div class="col">
          <h5>Insurance Back</h5>
          <img class="insurance-card" :src="backImageUrl">
        </div>
      </div>
    </div>
    <div slot="modal-footer" class="w-100">
      <button type="button" class="btn btn-orange float-right" @click.prevent="saveInsurance()"><font-awesome-icon size="lg" class="mr-2" v-show="isSaving" icon="spinner"  pulse/>Save</button>
      <button type="button" class="btn btn-outline-orange mr-2 float-right" @click.prevent="closeModal()">Close</button>
    </div>
  </b-modal>
</template>

<script>
  import _ from 'lodash'
  import moment from 'moment'
  import api from '../services/api'
  import Datepicker from 'vuejs-datepicker'
  import ErrorsMixin from '../mixins/ErrorsMixin'
  import AnalyticsMixin from '@/mixins/AnalyticsMixin'

  export default {
    name: 'EditInsuranceModal',

    mixins: [ErrorsMixin, AnalyticsMixin],

    components: { Datepicker },

    props: {
      visible: {
        type: Boolean,
        required: true
      },

      insuranceDetails: {
        type: Object,
        required: true
      },

      title: {
        type: String,
        default: 'Edit Insurance'
      },

      visitId: {
        type: Number,
        required: false
      }
    },

    data () {
      return {
        insurance: _.clone(this.insuranceDetails),
        frontImageUrl: '',
        backImageUrl: '',
        orgInsurancesCompanies: [],
        selectedInsuranceCompanyId: 0,
        isSaving: false,
        planSearchText: '',
        insuredContactTypes: [],
        yesNoOptions: [
          { value: true, text: 'Yes' },
          { value: false, text: 'No' }
        ],
        insuredContactTypeId: 0
      }
    },

    watch: {
      insuranceDetails (newValue, oldValue) {
        if (oldValue !== newValue) {
          this.insurance = _.clone(newValue)
          this.validateInsurance(false)
          this.selectedInsuranceCompanyId = this.insurance.organizationInsuranceCompanyId
          if (!this.insurance.organizationInsuranceCompanyId) {
            // See if we can match a single org insurance company to pre-select it
            const matchedCompanies = this.orgInsurancesCompanies.filter((orgInsCompany) => {
              return orgInsCompany.short === this.insurance.insuranceCompanyShort
            })

            if (matchedCompanies.length === 1) {
              this.setCompany(matchedCompanies[0])
            }
          }
          this.loadImages()
        }
      },

      visible (newValue, oldValue) {
        if (newValue) {
          this.loadContactTypes()
        }
      }

    },

    computed: {
      genderOptions () {
        return this.$store.genderOptions
      },

      individualDeductiblePercent () {
        return (this.insurance.verification.individualDeductibleMet / this.insurance.verification.individualDeductible) * 100
      },
      individualOutOfPocketPercent () {
        return (this.insurance.verification.individualMaxOopMet / this.insurance.verification.individualMaxOop) * 100
      },
      familyDeductiblePercent () {
        return (this.insurance.verification.familyDeductibleMet / this.insurance.verification.familyDeductible) * 100
      },
      familyOutOfPocketPercent () {
        return (this.insurance.verification.familyMaxOopMet / this.insurance.verification.familyMaxOop) * 100
      },

      insuranceCompanyName () {
        if (this.insurance.organizationInsuranceCompanyId) {
          return this.insurance.insuranceCompany
        } else {
          return 'Select Company'
        }
      },

      insuredContactTypeName () {
        if (this.insurance.insuredContactTypeId) {
          return this.insurance.insuredContactType
        } else {
          return 'Select Relationship'
        }
      },

      insurancePlanName () {
        if (this.insurance.organizationInsurancePlanId) {
          return this.insurance.insurancePlan
        } else {
          return 'Select Plan'
        }
      },

      insurancePlans () {
        const company = this.orgInsurancesCompanies.filter(item => {
          return item.organizationInsuranceCompanyId === this.insurance.organizationInsuranceCompanyId
        })[0]

        if (company && company.organizationInsurancePlans) {
          if (this.planSearchText) {
            const normalizedSearchText = this.planSearchText.toLowerCase()
            return company.organizationInsurancePlans.filter((plan) => {
              return plan.planNameFromOrg.toLowerCase().includes(normalizedSearchText) ||
                plan.planIdFromOrg.toLowerCase().includes(normalizedSearchText) ||
                (plan.street1 && plan.street1.includes(normalizedSearchText)) ||
                (plan.zip && plan.zip.includes(normalizedSearchText))
            })
          } else {
            return company.organizationInsurancePlans
          }
        }

        return []
      }
    },

    methods: {
      validateInsurance (forSave) {
        this.clearAllErrors()

        if (!forSave) {
          if (this.insurance.verification) {
            if (this.insurance.verification.verificationErrorField === 'InsuranceCompany' || !this.insurance.organizationInsuranceCompanyId) {
              this.addErrorForField('insuranceCompany', 'Insurance company mapping not found')
            }

            if (this.insurance.verification.verificationErrorField === 'InsuranceCompany' || !this.insurance.organizationInsurancePlanId) {
              this.addErrorForField('insurancePlan', 'Insurance plan mapping not found')
            }

            if (this.insurance.verification.verificationErrorField === 'PolicyNumber') {
              this.addErrorForField('subscriberNumber', 'Subscriber ID was not found')
            }
          }
        }

        if (this.insurance.patientIsSubscriber === false) {
          if (!this.insurance.insuredFirstName) {
            this.addErrorForField('insuredFirstName', 'Subscriber First Name is required.')
          }

          if (!this.insurance.insuredLastName) {
            this.addErrorForField('insuredLastName', 'Subscriber Last Name  is required.')
          }

          if (!this.insurance.insuredGenderId) {
            this.addErrorForField('insuredGenderId', 'Subscriber Gender  is required.')
          }

          if (!this.insurance.insuredBirthDate) {
            this.addErrorForField('insuredBirthDate', 'Subscriber Birth Date  is required.')
          }

          if (!this.insurance.insuredContactTypeId) {
            this.addErrorForField('insuredContactTypeId', 'Subscriber Relationship is required.')
          }
        }

        return !this.hasErrors()
      },

      clearPlanSearch () {
        this.planSearchText = ''
      },

      setContactType (contactType) {
        this.insurance.insuredContactTypeId = contactType.contactTypeId
        this.insurance.insuredContactType = contactType.name
      },

      loadContactTypes () {
        api.get('/Dashboard/ContactTypes').then(response => {
          this.insuredContactTypes = response.data
        }).catch(error => {
          if (!this.$store.canHandleNetworkError(error)) {
            this.setFromApiErrorResponse(error)
            this.$log.error('Error attempting to load insurance contact types: ' + error.message, error)
          }
        })
      },

      loadImages () {
        this.frontImageUrl = ''
        this.backImageUrl = ''

        api.get('/Dashboard/InsuranceCardPhotos/' + this.insurance.organizationPatientInsuranceId).then(response => {
          this.trackEvent('Insurance', 'EditModalLoadPhoto', 'ResponseTime', response.config.requestDuration)
          this.trackEvent('Insurance', 'EditModal', 'LoadPhoto', this.insurance.organizationPatientInsuranceId)

          this.frontImageUrl = response.data.front
          this.backImageUrl = response.data.back

        }).catch(error => {
          if (!this.$store.canHandleNetworkError(error)) {
            this.trackEvent('Insurance', 'EditModalLoadPhotoError', error.message)
            this.$log.error('Error attempting to load insurance photos: ' + error.message, error)
          }
        })
      },

      loadOrgInsurances () {
        api.get('/Dashboard/Organization/Insurances').then(response => {
          this.trackEvent('Insurance', 'EditModalLoadOrgIns', 'ResponseTime', response.config.requestDuration)
          response.data.sort((lhs, rhs) => {
            return (lhs.companyNameFromOrg > rhs.companyNameFromOrg) ? 1 : ((rhs.companyNameFromOrg > lhs.companyNameFromOrg) ? -1 : 0)
          })
          this.orgInsurancesCompanies = response.data
        }).catch(error => {
          if (!this.$store.canHandleNetworkError(error)) {
            this.trackEvent('Insurance', 'EditModalLoadOrgInsError', error.message)
            this.$log.error('Error attempting to load insurance companies: ' + error.message, error)
          }
        })
      },

      setCompany (insuranceCompany) {
        this.insurance.organizationInsuranceCompanyId = insuranceCompany.organizationInsuranceCompanyId
        this.insurance.insuranceCompany = insuranceCompany.name

        this.insurance.organizationInsurancePlanId = null
        this.insurance.insurancePlan = 'Select Plan'
      },

      setPlan (insurancePlan) {
        this.insurance.organizationInsurancePlanId = insurancePlan.organizationInsurancePlanId
        this.insurance.insurancePlan = insurancePlan.planNameFromOrg + ' (' + insurancePlan.planIdFromOrg + ')'
      },

      saveInsurance () {
        if (this.isSaving) {
          return
        }

        if (!this.validateInsurance(true)) {
          return
        }

        this.clearAllErrors()

        const policyNumber = this.insurance.policyNumber
        if (this.insurance.policyNumber) {
          this.insurance.policyNumber = policyNumber.trim()
        }
        const groupNumber = this.insurance.groupNumber
        if (this.insurance.groupNumber) {
          this.insurance.groupNumber = groupNumber.trim()
        }
        this.isSaving = true
        const viewModel = _.clone(this.insurance)
        viewModel.visitId = this.visitId
        api.post('/Dashboard/InsuranceDetail/' + this.insurance.organizationPatientInsuranceId, viewModel).then(response => {
          this.trackEvent('Insurance', 'EditModalSave', 'ResponseTime', response.config.requestDuration)
          this.trackEvent('Insurance', 'EditModal', 'Saved', this.insurance.organizationPatientInsuranceId)
          this.$emit('insuranceUpdated', true)
        }).catch(error => {
          if (!this.$store.canHandleNetworkError(error)) {
            this.trackEvent('Insurance', 'EditModalSaveError', error.message)
            this.$log.error('Error attempting to save insurance: ' + error.message, error)
          }
        }).then(() => {
          this.isSaving = false
        })
      },

      closeModal () {
        this.trackEvent('Insurance', 'EditModal', 'Closed')
        this.$emit('update:visible', false)
      },

      formatBirthDate (dob) {
        return moment(dob).utc().format('MM/DD/YYYY')
      },

      handleUserAuthenticated (authenticated) {
        if (authenticated) {
          this.loadOrgInsurances()
        }
      },

      handleOrgChange (newOrg, event) {
        this.loadOrgInsurances()
      }
    },

    created () {
      this.$store.$on('user:authenticated', this.handleUserAuthenticated)
      this.$store.$on('organization:changed', this.handleOrgChange)
      if (this.$store.isAuthenticated) {
        this.loadOrgInsurances()
      }
    }
  }
</script>

<style>
    .progress-orange {
        border-radius: .65em;
        border: 1px solid rgb(255,70,0);
        background-color: white;
    }
    .progress-orange .progress-bar {
        background-color: rgb(255, 70, 0);
    }

    .insurance-card {
        width: 300px;
    }

    .dropdown-container {
        max-height: 400px;
        overflow-y: auto;
    }
</style>
